import axios, { type InternalAxiosRequestConfig } from "axios";
import humps from "humps";
import { getHeaders, buildHeaderForUpload } from "../utils/headers";
import { retrieveAuthToken } from "../utils/authService";
import { isGuestAccessMode } from "../utils/guest";

const ApiRoutes = {
  Lessons: "/api/internal_api/v1/lessons",
  LessonFullDetails: "/api/internal_api/v1/levels/:levelId/lessons/:lessonId/full",
  LessonSignedTranscriptUrl: "/api/internal_api/v1/levels/:levelId/lessons/:lessonId/transcript",
  Assessment: "/api/internal_api/v1/assessments/:id",
};

const shouldRetryAuth = (config: InternalAxiosRequestConfig & { auth_retry?: number }) => {
  if (!config.auth_retry) {
    config.auth_retry = 3;
  }
  config.auth_retry -= 1;
  return config.auth_retry > 0;
};

export const getRailsClient = (
  options: { useToast?: boolean; forHeStatus?: boolean; noAuth?: boolean } = {
    useToast: true,
    forHeStatus: false,
    noAuth: false,
  },
  httpHeaders = getHeaders
) => {
  const client = axios.create({
    data: {},
    transformResponse: [
      ...(Array.isArray(axios.defaults.transformResponse) ? axios.defaults.transformResponse : []),
      data => humps.camelizeKeys(data),
    ],
    transformRequest: [
      data => humps.decamelizeKeys(data),
      ...(Array.isArray(axios.defaults.transformRequest) ? axios.defaults.transformRequest : []),
    ],
  });

  client.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      const status = error.response ? error.response.status : null;

      if (status === 401 && !options.noAuth && shouldRetryAuth(error.config)) {
        retrieveAuthToken({ forceRefresh: true }).then(() => {
          error.config.headers = httpHeaders();
          return client.request(error.config);
        });
      }

      return Promise.reject(error);
    }
  );

  client.interceptors.request.use(
    async config => {
      const headers = await httpHeaders(options.noAuth);
      Object.assign(config.headers, headers);
      if (isGuestAccessMode() && config.url) {
        config.url = config.url.replace("/internal_api", "/scorm");
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  return client;
};

export const getRailsFormClient = () => {
  const client = axios.create({
    transformResponse: [
      ...(Array.isArray(axios.defaults.transformResponse) ? axios.defaults.transformResponse : []),
      data => humps.camelizeKeys(data),
    ],
  });

  client.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      const status = error.response ? error.response.status : null;

      if (status === 401 && shouldRetryAuth(error.config)) {
        retrieveAuthToken({ forceRefresh: true }).then(() => {
          error.config.headers = buildHeaderForUpload();
          return client.request(error.config);
        });
      }

      return Promise.reject(error);
    }
  );

  client.interceptors.request.use(
    async config => {
      const headers = await buildHeaderForUpload();
      Object.assign(config.headers, headers);
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  return client;
};

export const getLessons = async () => {
  const client = getRailsClient();
  return client.get(ApiRoutes.Lessons);
};

export const getFullLessonDetails = async ({
  lessonId,
  levelId,
}: {
  lessonId: string;
  levelId?: string;
}) => {
  if (!lessonId) {
    return Promise.reject(new Error("getFullLessonDetails: lessonId is required"));
  }

  const client = getRailsClient();
  const url = ApiRoutes.LessonFullDetails.replace(":lessonId", lessonId.toString()).replace(
    ":levelId",
    levelId?.toString() || "undefined"
  );
  return client.get(url);
};

export const getLessonTranscriptUrl = async ({
  lessonId,
  levelId,
}: {
  lessonId: string;
  levelId?: string;
}) => {
  if (!lessonId) {
    return Promise.reject(new Error("getLessonTranscript: lessonId is required"));
  }

  const client = getRailsClient();
  const url = ApiRoutes.LessonSignedTranscriptUrl.replace(":lessonId", lessonId.toString()).replace(
    ":levelId",
    levelId?.toString() || "undefined"
  );
  return client.get(url).then(async response => {
    if (response.status === 200) {
      return response.data.transcriptUrl;
    } else {
      return Promise.reject(
        new Error(
          `getSignedTranscriptUrl: Unable to get signed transcript URL, status code ${response.status}`
        )
      );
    }
  });
};

// Fields accepted by the Rails AssessmentContract (assessments#update);
// camelCase keys are decamelized to snake_case by the request transform
export interface AssessmentUpdateParams {
  result?: "passed" | "failed" | "in_progress";
  beforeRating?: number;
  afterRating?: number;
  beforeRatedAt?: string;
  afterRatedAt?: string;
}

export const assessmentUpdate = async ({
  id,
  paramsToUpdate,
  useToast = false,
}: {
  id: number;
  paramsToUpdate: AssessmentUpdateParams;
  useToast?: boolean;
}) => {
  if (!id) {
    return Promise.reject(new Error("assessmentUpdate: id is required"));
  }
  if (!paramsToUpdate || Object.keys(paramsToUpdate).length === 0) {
    return Promise.resolve();
  }
  const client = getRailsClient({ useToast });
  const url = ApiRoutes.Assessment.replace(":id", id.toString());
  return client.put(url, { assessment: paramsToUpdate });
};
